@mixin translate($x, $y) {
  -webkit-transform: translate($x, $y);
  -ms-transform: translate($x, $y); // IE9+
  transform: translate($x, $y);
}

// Visually hidden but still announced by screen readers — for <label>s on
// selects/inputs whose visible UI (placeholder option text, icon-only
// buttons) doesn't otherwise expose an accessible name.
.sr_only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

// The vehicle-typeahead results list (Step1New, window.scs_autocomplete_ymm) is
// rendered through a React portal straight onto <body> — position:fixed with
// JS-computed top/left/width set inline (see VehicleTypeahead.tsx) — so these
// rules must live at module (top) scope, NOT nested under .vehicle_typeahead
// like the rest of that component's styles (.section__step .vehicle_typeahead
// input/icon/spinner). Portaling sidesteps a real stacking bug: step 1's
// section sits next to the (opacity < 1) "locked" step 2 section, which
// establishes its own stacking context — that made an absolutely-positioned
// dropdown nested inside step 1 paint *behind* step 2's text regardless of
// z-index, since z-index only orders elements within the same stacking context.
.vehicle_typeahead_results {
  position: fixed;
  z-index: 1000;
  max-height: 260px;
  overflow-y: auto;
  overscroll-behavior: contain;
  background-color: #fff;
  background-clip: padding-box;
  border: 1px solid #d3d3d3;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.16);
  list-style: none;
  margin: 0;
  padding: 6px;
  animation: vehicle_typeahead_fade_in 0.12s ease-out;
}

.vehicle_typeahead_result {
  padding: 9px 10px;
  font-size: 14px;
  color: #333;
  border-radius: 5px;
  cursor: pointer;
  transition: background-color 0.1s ease;

  &.vehicle_typeahead_result_active,
  &:hover {
    background-color: #f2f2f2;
  }
}

.vehicle_typeahead_empty {
  padding: 10px;
  font-size: 14px;
  color: #9b9b9b;
  font-style: italic;
  text-align: center;
}

@keyframes vehicle_typeahead_spin {
  to { transform: rotate(360deg); }
}

@keyframes vehicle_typeahead_fade_in {
  from { opacity: 0; transform: translateY(-4px); }
  to { opacity: 1; transform: translateY(0); }
}

/* Overlay loader used by Step1New when vehicle availability is loading */
.overlay_loader {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 60;
  border-radius: 5px;
}

.overlay_loader_inner {
  display: flex;
  align-items: center;
  gap: 12px;
}

.overlay_spinner {
  width: 36px;
  height: 36px;
  border: 4px solid #000;
  border-bottom-color: transparent;
  border-radius: 50%;
  box-sizing: border-box;
  animation: overlay-rotation 1s linear infinite;
}

@keyframes overlay-rotation {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.overlay_message {
  font-family: var(--font-family, 'sofia-pro, sans-serif');
  font-size: 16px;
  color: #000;
}

@mixin transition($transition) {
  -webkit-transition: $transition;
  transition: $transition;
}

@mixin transition-transform($transition) {
  -webkit-transition: transform $transition;
  -moz-transition: transform $transition;
  -o-transition: transform $transition;
  transition: transform $transition;
}

.form_control {
  font-family: system-ui, sans-serif;
  font-size: 1rem;
  font-weight: bold;
  line-height: 1.1;
  display: grid;
  grid-template-columns: 1em auto;
  gap: 0.3em;
  justify-content: center;
  align-items: center;
}

.form_control::-webkit-input-placeholder {
  color: rgb(117, 117, 117);
}


.section__step {
  position: relative;
  background: #f7f7f7;
  border-radius: 5px;
  padding: 15px;
  margin-bottom: 15px;

  @media (max-width: 767px) {
    padding: 10pxpx;
  }

  &.style01:not(.section_active) {
    opacity: 0.7;
    border-color: transparent !important;
  }

  &.section_locked {
    opacity: 0.7;
    pointer-events: none;
    -webkit-user-select: none;
    user-select: none;
  }

  h2 {
    margin: 0;
    position: relative;
    font-family: var(--font-family, "Roboto, sans-serif");
    font-size: 18px;
    font-weight: 700;
    line-height: 24px;
    text-align: left;
    color: #5C5C5C;
    margin-bottom: 10px;

    @media (max-width: 767px) {
      font-size: 14px;
    }

    strong {
      color: #000;
    }
  }

  .form_error {
    color: #f00;
  }

  .success {
    color: #00b902;
  }

  p {
    font-family: var(--font-family, "Roboto, sans-serif");
    font-size: 16px;
    font-weight: 400;
    line-height: 22.4px;
    text-align: left;
    @media (max-width: 767px) {
      font-size: 14px;
    }
  }


  .input_wrapper {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
    margin-bottom: 15px;

    .form_control_text {}

    input {
      width: 100%;
      border: 1px solid #d3d3d3;
      padding: 8px 10px 8px 10px;
      border-radius: 5px;
      font-family: var(--font-family, "Roboto, sans-serif");
      font-size: 16px;
      font-weight: 400;
      line-height: 22.4px;
      text-align: left;
      color: #5C5C5C;

      @media (max-width: 767px) {
        font-size: 14px;
      }

      &.error {
        border: 1px solid #f00;
      }
    }
  }

  .vehicle_selector {
    display: flex;
    justify-content: space-between;
    gap: 12px;

    &.has_trims {
      display: grid;
      grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr;
      grid-template-rows: 1fr 1fr;
      gap: 12px 12px;
      grid-template-areas:
        "year year year make make make make model model model model model"
        "trim trim trim trim trim trim cab cab cab cab cab cab";

      select {
        max-width: 100%;
      }

      select:nth-child(1) {
        grid-area: year;
      }

      select:nth-child(2) {
        grid-area: make;
      }

      select:nth-child(3) {
        grid-area: model;
      }

      select:nth-child(4) {
        grid-area: trim;
      }

      select:nth-child(5) {
        grid-area: cab;
      }
    }


    > select {
      position: relative;
      z-index: 1;
      flex: 1 1 30%;
      border: 1px solid #d3d3d3;
      padding: 8px 10px 8px 10px;
      border-radius: 5px;
      font-family: var(--font-family, "Roboto, sans-serif");
      font-size: 16px;
      font-weight: 400;
      line-height: 22.4px;
      text-align: left;
      color: #5C5C5C;
      max-width: 33%;

      @media (max-width: 767px) {
        font-size: 14px;
        height: 40px;
      }
    }

    .select_wrapper {
      flex: 1 1 30%;
      max-width: 33%;
      position: relative;

      &:after {
        position: absolute;
        content: "";

        background-image: url("data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2215%22%20height%3D%229%22%20viewBox%3D%220%200%2015%209%22%20fill%3D%22none%22%3E%3Cpath%20fill-rule%3D%22evenodd%22%20clip-rule%3D%22evenodd%22%20d%3D%22M0.274589%200.274547C0.640708%20-0.0915156%201.2343%20-0.0915156%201.60041%200.274547L7.1875%205.86167L12.7746%200.274547C13.1407%20-0.0915156%2013.7343%20-0.0915156%2014.1004%200.274547C14.4665%200.640672%2014.4665%201.2343%2014.1004%201.60042L7.85044%207.85042C7.48431%208.21648%206.89069%208.21648%206.52456%207.85042L0.274589%201.60042C-0.0915297%201.2343%20-0.0915297%200.640672%200.274589%200.274547Z%22%20fill%3D%22black%22%2F%3E%3C%2Fsvg%3E");
        background-repeat: no-repeat;
        background-size: contain;
        display: block;
        width: 14.375px;
        height: 8.125px;
        border-radius: 0.65em;
        pointer-events: none;
        z-index: 2;
        right: 8px;
        top: 50%;
        margin-top: -4px;
        object-fit: contain;

        @media (max-width: 767px) {
          width: 9.823px;
          height: 5.552px;
          right: 10px;
          margin-top: -2px;
        }
      }

      select {
        position: relative;
        z-index: 1;
        flex: 1 1 100%;
        border: 1px solid #d3d3d3;
        padding: 8px 10px 8px 10px;
        border-radius: 5px;
        font-family: var(--font-family, "Roboto, sans-serif");
        font-size: 16px;
        font-weight: 400;
        line-height: 22.4px;
        text-align: left;
        color: #5C5C5C;
        width: 100%;
        -webkit-appearance: none;
        -moz-appearance: none;

        @media (max-width: 767px) {
          font-size: 14px;
          width: 100% !important;
          height: 40px;
        }
      }


    }

    &.has_trims {

      .select_wrapper {
        max-width: 100%;
      }

      .select_wrapper:nth-child(1) {
        grid-area: year;
      }

      .select_wrapper:nth-child(2) {
        grid-area: make;
      }

      .select_wrapper:nth-child(3) {
        grid-area: model;
      }

      .select_wrapper:nth-child(4) {
        grid-area: trim;
      }

      .select_wrapper:nth-child(5) {
        grid-area: cab;
      }
    }
  }

  .vehicle_typeahead {
    // Explicit z-index (not just position:relative) forces this to establish
    // its own stacking context, so the dropdown below is guaranteed to paint
    // above whatever follows in the page (e.g. the next step's section) —
    // relying on "positioned but z-index:auto" here would leave that ordering
    // to coincidence.
    position: relative;
    z-index: 5;

    input {
      position: relative;
      z-index: 1;
      border: 1px solid #d3d3d3;
      padding: 8px 36px 8px 34px;
      border-radius: 5px;
      font-family: var(--font-family, "Roboto, sans-serif");
      font-size: 16px;
      font-weight: 400;
      line-height: 22.4px;
      text-align: left;
      color: #333;
      background-color: #fff;
      width: 100%;
      transition: border-color 0.15s ease;

      &::placeholder {
        color: #9b9b9b;
      }

      &:focus {
        outline: none;
        border-color: #999;
      }

      @media (max-width: 767px) {
        font-size: 14px;
        height: 40px;
      }
    }

    .vehicle_typeahead_icon {
      position: absolute;
      z-index: 2;
      left: 10px;
      top: 50%;
      transform: translateY(-50%);
      width: 16px;
      height: 16px;
      pointer-events: none;
      color: #9b9b9b;

      svg {
        width: 100%;
        height: 100%;
      }
    }

    .vehicle_typeahead_spinner {
      position: absolute;
      z-index: 2;
      right: 10px;
      top: 50%;
      width: 16px;
      height: 16px;
      margin-top: -8px;
      border: 2px solid #d3d3d3;
      border-top-color: #757575;
      border-radius: 50%;
      animation: vehicle_typeahead_spin 0.7s linear infinite;
    }

  }

  .savings {
    margin-top: 10px;
    background-color: #FCF9CD;
    padding: 16px 5px;
    border-radius: 5px;
    font-family: var(--font-family, "Roboto, sans-serif");
    font-size: 12px;
    font-weight: 600;
    text-align: left;
    display: flex;
    align-content: center;
    justify-content: center;
    align-items: center;
    gap: 7px;

    @media (max-width: 767px) {
      padding: 12px 5px;
    }

    .savings_price {}
  }

  .summary {
    display: flex;
    justify-content: space-between;

    &:before {
      content: url("data:image/svg+xml,%3Csvg%20width%3D%2212%22%20height%3D%2211%22%20viewBox%3D%220%200%2012%2011%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cpath%20d%3D%22M5.63828%2010.3054L0.910156%206.62443L2.25453%204.90025L5.15797%207.16064L9.94984%200.250488L11.748%201.4953L5.63828%2010.3054Z%22%20fill%3D%22white%22%2F%3E%3C%2Fsvg%3E");
      position: absolute;
      top: 0;
      right: 0;
      background-color: #00b902;
      border-radius: 0 5px 0 5px;
      color: #fff;
      width: 30px;
      height: 30px;
      display: flex;
      align-content: center;
      align-items: center;
      justify-content: center;
    }

    .selected_value {
      font-family: var(--font-family, "Roboto, sans-serif");
      font-size: 14px;
      font-weight: 400;
      line-height: 24px;
      text-align: left;
      color: #000;
      border-radius: 5px;
      padding: 1px 10px 1px 10px;
      background-color: #fff;
      @media (max-width: 767px) {
        font-size: 12px;
      }
    }

    .btn_change {
      cursor: pointer;
      font-family: var(--font-family, "Roboto, sans-serif");
      font-size: 14px;
      font-weight: 400;
      line-height: 24px;
      text-align: left;
      color: #ee3935;
      text-decoration: underline;
      border: 0;
      background-color: transparent;
      white-space: nowrap;
      position: relative;
      top: -35px;
      @media (max-width: 767px) {
        font-size: 12px;
      }
    }
  }

  &#step_3 {
    .summary {
      display: none;
    }
  }
}

.btn {
  cursor: pointer;
  display: flex;
  background: #056906;
  padding: 10px 35px 10px 35px;
  gap: 10px;
  border-radius: 5px;
  border: 0;
  color: #fff;
  font-family: var(--font-family, "Roboto, sans-serif");
  font-size: 18px;
  font-weight: 800;
  line-height: 27px;
  text-align: center;
  transition: transform .2s, background-color .2s cubic-bezier(.25, 1, .5, 1);
  box-shadow: 0 4px #098010;

  &:not([disabled]):hover {
    box-shadow: none;
    background-color: #098010;
    transform: translateY(4px);
    color: #fff;
  }

  .compare {
    font-weight: 400;
  }

  @media (max-width: 767px) {
    display: block;
    width: 100%;
    text-align: center;
  }
}

.btn[disabled] {
  opacity: 0.5;
}

.btn_inner {
  display: flex;
  gap: 10px;
  align-items: center;
  justify-content: center;
  @media (max-width: 767px) {
    width: 100%;
  }
}

.seat_type_selector {
  display: grid;
  grid-auto-rows: 1fr;
  grid-template-columns: repeat(auto-fill, 50%);
  align-items: center;
}

.seat_type_selector {
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.button_option {
  display: flex;
  height: 100%;
  align-content: center;
  align-items: center;
  flex-grow: 1;

  label {
    width: 100%;
    align-items: center;
    height: 100%;
    position: relative;
    background-color: #eaeaea;
    padding: 16px 10px;
    border-radius: 5px;
    display: flex;
    justify-content: space-between;
    border: 2px solid transparent;
    @media (max-width: 767px) {
      padding: 5px 6px;

    }

    .content_wrapper {
      display: flex;
      margin-left: 0;
      margin-right: auto;
      gap: 5px;
      width: 100%;
      align-content: center;
      align-items: center;
      justify-content: flex-start;
      flex-wrap: wrap;
      flex-direction: row;

      @media (max-width: 767px) {
        margin-left: 0;
        margin-right: auto;
        flex-direction: column;
        align-items: self-start;
      }
    }

    .title_wrapper {
      flex-grow: 1;

      .title {
        font-family: var(--font-family, "Roboto, sans-serif");
        font-size: 15px;
        font-weight: 800;
        line-height: 20.7px;
        text-align: left;

        color: #171c28;
        @media (max-width: 767px) {
          font-size: 13px;
        }
      }

      .sub_title {
        font-family: var(--font-family, "Roboto, sans-serif");
        font-size: 12px;
        font-weight: 500;
        line-height: 16.1px;
        text-align: left;
        color: #8b8b8b;
        @media (max-width: 767px) {
          font-size: 10px;
        }
      }
    }

    .price_wrapper {
      padding: 4px 10px 4px 10px;
      border-radius: 100px;
      background: #ffffff;
      font-family: var(--font-family, "Roboto, sans-serif");
      font-size: 12px;
      font-weight: 800;
      line-height: 16.1px;
      text-align: left;
      color: #717171;
      display: inline-flex;
      margin-block: auto;
      margin-left: auto;
      margin-right: 0;
      gap: 5px;
      @media (max-width: 767px) {
        padding: 1px 5px;
        text-align: center;
        flex-grow: 0;
        margin-top: 0;
        font-size: 12px;
        flex-shrink: 1;
        width: fit-content;
        gap: 5px;
        display: flex;
        flex-direction: row;
        margin-left: 0;
      }

      .compare_price {
        font-family: var(--font-family, "Roboto, sans-serif");
        font-size: 12px;
        font-weight: 500;
        line-height: 18.4px;
        text-align: left;
        text-decoration: line-through;
        @media (max-width: 767px) {
          font-size: 12px;
        }
      }

      .compare_price + .price {
        color: #1db936;
        line-height: 18.4px;
      }
    }
  }
}

.button_option {
  label {
    cursor: pointer;
    border: 1px solid #cfcfcf;
    outline: 1px solid transparent;
    background: #F0F0F0;

  }


  &.checked label, label:has(input:checked) {
    border: 1px solid #BBB;
    outline: 1px solid #BBB;
    background: #EAEAEA;

    &::before {
      content: url("data:image/svg+xml,%3Csvg%20width%3D%2212%22%20height%3D%2211%22%20viewBox%3D%220%200%2012%2011%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cpath%20d%3D%22M5.63828%2010.3054L0.910156%206.62443L2.25453%204.90025L5.15797%207.16064L9.94984%200.250488L11.748%201.4953L5.63828%2010.3054Z%22%20fill%3D%22white%22%2F%3E%3C%2Fsvg%3E");
      position: absolute;
      top: -10px;
      right: -10px;
      background-color: #00b902;
      border-radius: 50%;
      color: #fff;
      width: 20px;
      height: 20px;
      line-height: 8px;
      display: flex;
      align-content: center;
      align-items: center;
      justify-content: center;
    }
  }
}

.grid_3 {
  gap: 10px;
  grid-template-columns: repeat(4, 1fr);
  grid-template-rows: 1fr;

  .button_option:nth-child(1) {
    grid-area: 2 / 1 / 3 / 5;
    order: 3;
    @media (max-width: 767px) {
      label {
        padding: 10px 6px;
      }
      .content_wrapper {
        flex-direction: row;
      }
      .title_wrapper {
        display: flex;
        flex-direction: row;
        align-items: center;
        align-content: center;
        gap: 5px;
      }
      .title {
        white-space: nowrap;
      }
      .sub_title {
        white-space: nowrap;
      }
    }
  }

  .button_option:nth-child(2) {
    grid-area: 1 / 1 / 2 / 3;
    order: 1;
  }

  .button_option:nth-child(3) {
    grid-area: 1 / 3 / 2 / 5;
    order: 2;
  }
}

// The Jump Seat Cover addon (see Step2.tsx) is spliced into the seat option
// list right after whichever seat it applies to, so its position among
// .button_option siblings shifts depending on which seat matched (First Row,
// 2 Row Bundle, 3 Row Bundle). That breaks the position-based nth-child rules
// above (.grid_3) and in .has_bf_2025 .seat_type_selector below, which assume
// a fixed 3-item layout. Always render it full-width regardless of position.
.button_option.addon_full_width {
  grid-area: auto !important;
  grid-column: 1 / -1 !important;
  width: 100% !important;

  // .has_bf_2025 .seat_type_selector .button_option label::before below sets
  // display:none on every card's checkmark badge (it's meant to hide the
  // native radio/checkbox indicator in favor of the "Best Value"/"Custom"
  // ribbons). That rule has higher specificity than the base checked-state
  // badge, so without this override the addon would never show its checkmark
  // when selected. Only `display` needs re-enabling — content/position/etc.
  // still come from the base .button_option "&.checked label" rule.
  &.checked label::before,
  label:has(input:checked)::before {
    display: flex !important;
  }

  // The native <input type="checkbox"> itself (inside .form_control) has no
  // drawn checkmark anywhere in this stylesheet — it renders as a bare
  // browser-default checkbox. Draw one explicitly so it visibly ticks when
  // checked. `position: static !important` overrides
  // .has_bf_2025 .seat_type_selector .button_option input[type=checkbox]::before,
  // which otherwise pins this pseudo-element to top:1px/left:1px instead of
  // letting the grid on the input center it.
  .form_control input[type="checkbox"] {
    -webkit-appearance: none;
    appearance: none;
    margin: 0;
    width: 1.15em;
    height: 1.15em;
    border: 0.15em solid #8b8b8b;
    border-radius: 0.2em;
    background: #fff;
    display: grid;
    place-content: center;
    cursor: pointer;

    &::before {
      content: "";
      position: static !important;
      width: 0.65em;
      height: 0.65em;
      clip-path: polygon(14% 44%, 0 65%, 50% 100%, 100% 16%, 80% 0%, 43% 62%);
      transform: scale(0);
      transition: transform 120ms ease-in-out;
      background-color: #05690E;
    }

    &:checked {
      border-color: #05690E;

      &::before {
        transform: scale(1);
      }
    }
  }
}

//
// Modal
// ----------------------

.modal {

  position: relative;
  // This is modal bg
  &:before {
    content: "";
    display: none;
    background: rgba(0, 0, 0, .6);
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 100;
  }

  &:target {

    // Active animate in modal
    &:before {
      display: block;
    }

    .modalDialog {
      @include translate(0, 0);
    }

  }

}

// Modal Dialog
// ----------------------

.modalDialog {
  background: #fefefe;
  border: #333 solid 1px;
  border-radius: 5px;
  margin-top: 0;
  position: fixed;
  left: 10px;
  height: calc(100% - 20px);
  top: 10px;
  z-index: 101;
  max-width: 360px;
  width: 100%;
  @include translate(-200%, 0);
  @include transition-transform(0.3s ease-in);
}

.modalBody {
  padding: 20px;
}

.btnClose {
  position: absolute;
  top: 15px;
  text-decoration: none;
  right: 15px;
}

.modalHeader,
.modalFooter {
  padding: 10px 20px;
}

.modalHeader {
  border-bottom: #eee solid 1px;

  h2 {
    font-size: 20px;
  }

}

.modalFooter {
  border-top: #eee solid 1px;
  text-align: right;
}

.custom_solution {
  position: absolute;
  top: 0;
  right: 20px;
  font-family: var(--font-family, "Roboto, sans-serif");
  font-size: 14px;
  font-weight: 400;
  line-height: 24px;
  text-align: left;
  color: #ee3935;
  text-decoration: underline;
  border: 0;
  background-color: transparent;
  white-space: nowrap;
  @media (max-width: 767px) {
    font-size: 12px;
  }
}

.vehicle_alert {
  margin-top: 0;
  font-family: var(--font-family, "Roboto, sans-serif");
  font-size: 14px;
  font-weight: 400;
  line-height: 24px;
  text-align: left;

  strong {
    color: #ee3935;
    font-weight: bold;
    margin-top:0 !important;
  }
}

.custom_alert {
  background-color: #D6F0FF;
  border-left: 6px solid #72C8FF;
  border-radius: 5px;
  padding: 20px 50px;

  p {
    font-family: var(--font-family, "Roboto, sans-serif");
    font-size: 14px;
    font-weight: 400;
    line-height: 24px;
  }

  .btn {
    text-decoration: none;
    text-align: center;
    width: 100%;
    justify-content: center;
  }
}

.style01 {
  .section__step {
    border: 2px solid #0A8010;

    .vehicle_selector {
      select:not([disabled]) {
        background-color: #FFF5E3;
        border: 1px solid #848484;
      }

      select[disabled] {
        background-color: #FFFFFF;
        border: 1px solid #D3D3D3;
        color: #B0B0B0;
      }
    }

    .bottom_text {
      margin-top: 8px;
      color: #000;
      text-align: center;
      leading-trim: both;
      text-edge: cap;
      font-family: "Roboto", sans-serif;
      font-size: 12px;
      font-style: normal;
      font-weight: 400;
      line-height: 24px; /* 200% */
      display: flex;
      justify-content: space-between;
      align-items: center;

      div {
        span {
          color: #E01B1A;
        }

        strong {
          font-weight: normal;
          color: #0A8010;
        }
      }
    }
  }

  &.button_option {
    label:has(input:checked) {
      border: 1px solid #BBB;
      outline: 1px solid #BBB;
      background: #FFF5E3;
    }
  }

  &.checked label {
    border: 1px solid #BBB;
    outline: 1px solid #BBB;
    background: #FFF5E3;
  }

}

.btn_savings {
  display: none;
}

.style01 {
  .btn_savings {
    display: flex;
    padding: 5px;
    justify-content: center;
    align-items: center;
    gap: 5px;
    margin: 0 auto;
    background: #1DB936;
    color: #FFF;
    leading-trim: both;
    text-edge: cap;
    font-family: "Roboto", sans-serif;
    font-size: 12px;
    line-height: 12px;
    font-style: normal;
    font-weight: 400;
    text-transform: capitalize;
    @media (max-width: 767px) {
      margin-left: 0;
    }
  }


  .btn {
    width: 100%;
    text-align: center;

    .btn_inner {
      width: 100%;
    }
  }
}

.stock_alert {
  margin-top: 10px;
  background-color: #FCF9CD;
  padding: 5px 10px;
  border-radius: 5px;
  font-family: var(--font-family, "Roboto, sans-serif");
  font-size: 12px;
  font-weight: 600;
  text-align: left;
  display: flex;
  align-content: flex-start;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 7px;
  color: #333;
  @media (max-width: 767px) {
    padding: 5px 10px;
  }

  .text {
    /*animation: pulse 2s infinite;*/
    display: flex;
    align-content: flex-start;
    justify-content: flex-start;
    align-items: flex-start;
    gap: 7px;
  }

}

@keyframes stepFadeInUp {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes stepCheckmarkIn {
  0%   { transform: scale(0.5); opacity: 0; }
  60%  { transform: scale(1.15); opacity: 1; }
  100% { transform: scale(1); opacity: 1; }
}

@keyframes pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.07);
  }
  100% {
    transform: scale(1);
  }
}

.priceContainer {
  display: inline-flex;
  gap: 5px;
  justify-content: center;
  align-items: center;
}

.has_ultimate {
  .button_option:nth-child(2) {
    grid-area: 1 / 1 / 2 / 5;
    order: 1;
  }
}

.section__step_ult {
  border-style: dashed !important;

  .button_option {
    height: auto;
  }

  .priceContainer {
    flex-direction: column;
  }

}

.ultimate_wrapper {
  display: flex;
  gap: 15px;
  margin: 10px 0;
  align-items: center;
  justify-content: flex-start;

  img {
    max-width: 118px;
    aspect-ratio: 276 / 340;
    flex-shrink: 0;
  }
}

.ultimate_benefits {
  margin: 0;
  color: #000;
  display: flex;
  padding: 16px 18px 16px 38px;
  justify-content: center;
  align-items: self-start;
  flex-direction: column;
  gap: 0;
  border-radius: 5px;
  background: #E6F1FA;

  li {
    font-family: var(--font-family, "Roboto, sans-serif");
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
    text-transform: capitalize;
  }
}

.ultimate_upgrade {
  .btn_savings {
    border-radius: 5px;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    gap: 6px;
    background-color: #797979;
    color: #fff;
  }

  .price_wrapper {
    border-radius: 100px;
    background-color: #1DB936 !important;
    display: flex;
    padding: 5px;
    justify-content: flex-end;
    align-items: center;
    gap: 3px;

    .price {
      color: #fff !important;
      color: #FFF;

      font-size: 12px;
      font-style: normal;
      font-weight: 800;
      line-height: normal;
      text-transform: capitalize;
    }
  }
}

.bf_heading {
  color: #FFF;
  font-family: var(--font-family, "Roboto, sans-serif");
  font-size: 15px;
  line-height: 1em;
  font-style: normal;
  font-weight: 500;
  text-transform: capitalize;
  display: inline-flex;
  padding: 5px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  background: #000;
  margin: 0 0 5px 0;
}

.list_items {
  margin: 0 0 0 15px;
  display: grid;
  grid-template-columns:1fr 1fr;
  grid-auto-rows: 1fr;
  padding: 0;
  color: #000;
  font-family: var(--font-family, "Roboto, sans-serif");
  font-size: 10px;
  font-style: normal;
  font-weight: 500;
  line-height: 18px; /* 180% */
  text-transform: capitalize;
}

.bf_wrapper {
  margin-top: 10px;
  border-radius: 5px;
  border: 1px dashed #CFCFCF;
  display: flex;
  padding: 8px 18px 10px 18px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 10px;
  align-self: stretch;
  margin-bottom: 15px;
  @media (max-width: 767px) {
    padding: 8px 8px 10px 8px;
  }

  h5 {
    color: #000;
    text-align: center;
    font-family: var(--font-family, "Roboto, sans-serif");
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
    letter-spacing: 0.12px;
    margin: 0;

    strong {
      color: #D61E2D;
      display: block;
    }
  }

  .bf_items {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    list-style: none;
    margin: 0;
    padding: 0;
    color: #000;
    font-family: var(--font-family, "Roboto, sans-serif");
    font-size: 13px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
    letter-spacing: 0.08px;
    gap: 8px;
    @media (max-width: 390px) {
      font-size: 12px;
    }

    li {
      flex: 1 1 calc((100% - 24px) / 4);
      min-width: calc((100% - 24px) / 4);
      max-width: calc((100% - 24px) / 2);
      text-align: center;
      display: flex;
      flex-direction: column;
      justify-content: flex-start;
      align-items: center;
      gap: 5px;

      strong {
        display: block;
        margin: 0;
        line-height: 1em;
      }

      s {
        display: inline !important;
      }

      .img_wrapper {

        border-radius: 5px;
        background: linear-gradient(180deg, #D8C4AD 0%, #E5DDD0 100%);
        display: flex;
        align-content: center;
        justify-content: center;
        align-items: center;
        position: relative;
        width: 100%;
        aspect-ratio: 1 / 1;

        img {
          width: 90%;
          transform: translateX(3px) translateY(10px);
        }
      }
    }
  }
}

.scs_father_day_2026 {
  font-family: Roboto, var(--font-family, "Roboto, sans-serif");

  h1, h2, h3, h4, h5, h6, strong,
  .title_wrapper .title,
  .button_option .title {
    font-family: Oswald, var(--font-family, "Roboto, sans-serif") !important;
    text-transform: uppercase !important;
  }
}

.bf_wrapper.bf_wrapper_father_day_2026 {
  background: #F7F7F7;
  border: 1px dashed #CFCFCF;

  .bf_items li:nth-child(2) .img_wrapper img,
  .bf_items li:nth-child(4) .img_wrapper img {
    --img-offset-y: 8%;
  }

  .bf_items li:nth-child(3) .img_wrapper img {
    --img-offset-y: 0%;
  }

  .bf_items li strong {
    letter-spacing: -0.02px;
  }

  .bf_items li .img_wrapper {
    border-radius: 7.397px;
    background: linear-gradient(180deg, #B0B0B0 0%, #E9E9E9 100%), linear-gradient(180deg, #D8C4AD 0%, #E5DDD0 100%);
    width: 100%;
    aspect-ratio: 1 / 1;
    position: relative;
    overflow: hidden;

    img {
      --img-offset-x: 0%;
      --img-offset-y: 6%;
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(calc(-50% + var(--img-offset-x)), calc(-50% + var(--img-offset-y)));
      width: auto;
      height: auto;
      max-width: 90%;
      max-height: 90%;
      object-fit: contain;
      object-position: center;
      display: block;
    }
  }

  .bf_items li > span {
    letter-spacing: 0px;

    s {
      color: #D61E2D;
    }

  }

  h5 {
    color: #000;
    text-align: center;

    font-family: Roboto, var(--font-family, "Roboto, sans-serif");
    font-size: 14px;
    font-style: normal;
    font-weight: 600;
    line-height: 17px;
    letter-spacing: normal;

    strong {

      display: block;
      color: #000;
      font-family: Oswald;
      font-size: 18px;
      font-style: normal;
      font-weight: 700;
      line-height: 17px;
      text-transform: uppercase;

      em {
        color: #D61E2D;
      }
    }

    em {
      color: #D61E2D;
      font-style: normal;
    }

    :global(.scs-sweepstakes-tooltip) {
      text-align: center;
      font-family: Oswald, var(--font-family, "Roboto, sans-serif");
      font-size: 18px;
      font-style: normal;
      font-weight: 700;
      line-height: 17px;
      text-transform: uppercase;
    }
  }

  .bf_warranty_bar {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 8px 8px 8px 16px;
    border-radius: 8px;
    background: linear-gradient(0deg, #E5E5E5 0%, #CFCFCF 100%);
    color: #000;
    font-family: Oswald, var(--font-family, "Roboto, sans-serif");
    font-size: 14px;
    font-weight: 700;
    line-height: 1em;
    letter-spacing: 0.12px;
    text-transform: uppercase;
    @media (max-width: 390px) {
      font-size: 12px;
      padding: 6px 6px 6px 10px;
      gap: 6px;
    }

    span, s, strong, em {
      font-family: Oswald, var(--font-family, "Roboto, sans-serif") !important;
      text-transform: uppercase !important;
    }
  }

  .bf_warranty_icon {
    font-size: 22px;
    line-height: 1em;
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
  }

  .bf_warranty_icon_img {
    width: 22px;
    height: 22px;
    object-fit: contain;
    display: block;
  }

  .bf_warranty_text {
    text-transform: uppercase;
    flex-shrink: 0;
  }

  .bf_warranty_msrp {
    color: #000;

    font-weight: 700;
    flex-shrink: 0;

    s {
      color: #D61E2D;
      font-weight: 700;
    }
  }

  .bf_warranty_badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 8px 14px;
    border-radius: 6px;
    background: #D61E2D;
    color: #FFF;
    font-weight: 900;
    font-size: 14px;
    line-height: 1em;
    letter-spacing: 0.5px;
    flex-shrink: 0;
    @media (max-width: 390px) {
      padding: 6px 10px;
      font-size: 12px;
    }
  }
}

.bf_choose_heading {
  text-align: center;
  position: relative;

  &:before {
    content: '';
    display: block;
    position: absolute;
    top: 50%;
    left: 0;
    width: 100%;
    height: 0;
    border-bottom: 1px solid #000;
  }

  span {
    position: relative;
    z-index: 1;
    padding-inline: 5px;
    background-color: #F7F7F7;
    color: #000;
    font-family: var(--font-family, "Roboto, sans-serif");
    font-size: 13px;
    font-style: normal;
    font-weight: 500;
    line-height: 18px; /* 180% */
    text-transform: capitalize;
    text-align: center;
  }
}

.innerPriceContainer {
  display: flex;
  flex-direction: row;
  gap: 5px;
}

.has_bf_2025 {
  .bottom_text {

    span {
      color: #E67E22 !important;

    }

    @media (max-width: 767px) {
      line-height: 12px !important;
    }
  }

  .seat_type_selector {
    display: flex;
    flex-direction: column;

    .button_option {
      input[type=checkbox], input[type=radio] {
        position: relative;
      }
      input[type=checkbox]::before, input[type=radio]::before {
        --form-control-color: #05690E;
        position: absolute;
        top: 1px;
        left: 1px;
      }

      .priceContainer {
        display: flex;
        flex-direction: column;
      }

      .innerPriceContainer {
        border-radius: 100px;
        background: #FFF;
        padding: 5px 8px;
        display: flex;
        align-content: center;
        align-items: center;

      }

      .btn_savings {
        order: 2;
        margin: 0;
        background: #000;
        display: flex;
        padding: 3px 5px;
        line-height: 1em;
        justify-content: center;
        align-items: center;
        gap: 5px;
        color: #FFF;
        font-family: var(--font-family, "Roboto, sans-serif");
        font-size: 10px;
        font-style: normal;
        font-weight: 400;
        text-transform: capitalize;
        flex-shrink: 1;
        flex-grow: 0;
        height: fit-content;
      }

      .price_wrapper {
        color: #8E8E8E;
        font-family: var(--font-family, "Roboto, sans-serif");
        font-size: 16px;
        font-style: normal;
        font-weight: 500;
        line-height: normal;
        text-transform: capitalize;
        display: flex;
        align-items: center;
        align-content: center;
        margin: 0;
        padding: 0;

        .compare_price {
          font-size: 16px;
        }

        .price {
          color: #D61E2D;
          font-size: 18px;
          font-style: normal;
          font-weight: 900;
        }
      }

      .title {
        color: #171C28;
        font-family: var(--font-family, "Roboto, sans-serif");
        font-size: 15px;
        font-style: normal;
        font-weight: 800;
        line-height: normal;
        text-transform: capitalize;
      }

      .sub_title {
        display: none;
      }

      &.custom_rear .sub_title {
        display: block;
      }

      label {
        padding-block: 20px;
      }

      &.checked label {
        border-radius: 5px;
        border: 1px solid #CFCFCF;
        background: #E6F1FA;
      }
    }

    .button_option {
      label {
        &::before {
          z-index: 3;
          display: none;
        }

        @media (max-width: 767px) {
          .content_wrapper {
            flex-direction: row;
          }
        }
      }
    }



    .button_option:nth-child(1) {
      order: 0 !important;
      width: 100%;
      position: relative;
      @media (max-width: 767px) {
        .title_wrapper {
          flex-direction: column;
          align-items: flex-start;
          align-content: flex-start;
          gap: 0;
          justify-content: center;
        }
      }

      label {
        position: relative;
        z-index: 1;


        &::before {
          z-index: 3;
          display: none;
        }

        &::after {
          content: '🏆 Best Value';
          position: absolute;
          top: -2px;
          right: -2px;
          color: #FFF;
          font-family: var(--font-family, "Roboto, sans-serif");
          font-size: 10px;
          font-style: normal;
          font-weight: 700;
          line-height: normal;
          text-transform: capitalize;
          border-radius: 0 5px;
          background: #D61E2D;
          display: inline-flex;
          padding: 1px 5px;
          justify-content: center;
          align-items: center;
          gap: 10px;
          z-index: 1;
        }
      }


    }

    .button_option.custom_rear {
      label {
        position: relative;
        z-index: 1;

        &::before {
          z-index: 3;
          display: none;
        }

        &::after {
          content: 'Custom';
          position: absolute;
          top: -2px;
          right: -2px;
          color: #FFF;
          font-family: var(--font-family, "Roboto, sans-serif");
          font-size: 10px;
          font-style: normal;
          font-weight: 700;
          line-height: normal;
          text-transform: capitalize;
          border-radius: 0 5px;
          background: #D61E2D;
          display: inline-flex !important;
          padding: 1px 5px;
          justify-content: center;
          align-items: center;
          gap: 10px;
          z-index: 1;
        }
      }
    }

    .button_option:nth-child(2) {
      order: 1 !important;
      width: 100%;
    }

    .button_option:nth-child(3) {
      order: 2 !important;
      width: 100%;
    }
  }

  .selector_wrapper:has(.button_option.custom_rear) .button_option:not(.custom_rear) label::after {
      display: none !important;
    }
}

.special_price_title {
  text-align: center;
  display: block;
  color: #2B2B2B;
  font-family: var(--font-family, "Roboto, sans-serif");
  font-size: 10px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  letter-spacing: 0.09px;
  text-transform: uppercase;
}

.title_label {
  color: #FFF;
  font-family: var(--font-family, "Roboto, sans-serif");
  font-size: 11px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  text-transform: capitalize;
  display: inline-flex;
  padding: 2px;
  justify-content: flex-end;
  align-items: center;
  background-color: #000;
}

[class^="_section__product_top"] {
  [class^="_color_option"] {
    &[class*="_checked"] {
      label {
        &:before {
          display: none;
        }

        [class^='_color_img'] {
          outline: 3px solid #ED1B34;
        }
      }
    }

    [class^='_title'] {
      font-size: 18px;
      @media (min-width:768px) and (max-width:991px) {
        font-size: 14px;
      }
    }
  }
}

.section__step.scs_new_design_2026 {
  display: flex;
  flex-direction: row;
  gap: 10px;
  background-color: transparent;
  transition: opacity 0.3s ease;

  @media (max-width: 767px) {
    gap: 5px;
  }

  &.section__step.style01:not(.section_active) {
    opacity: 1;
  }

  &.section__step.style01 {
    border: 0;
  }

  &.section__step {
    border: 0;
    padding: 0;
    margin-bottom: 5px;
  }

  &.section__step {
    .summary:before {
      display: none;
    }
  }

  h2 {
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
    gap: 5px;
    /* @media (max-width: 767px) {
      display: none;
    } */

    .section__step_no {
      width: 44px;
      height: 44px;
      color: #fff;
      background-color: #000;
      opacity: 1 !important;
      text-align: center;
      display: flex;
      border-radius: 100%;
      align-items: center;
      justify-content: center;
      line-height: 0;
      flex-grow: 0;
      flex-shrink: 0;
      @media (max-width: 767px) {
        width: 28px;
        height: 28px;
      }

      &.section__step_no_done {
        animation: stepCheckmarkIn 0.45s cubic-bezier(0.34, 1.56, 0.64, 1) both;
        font-size: 0 !important;

        &::after {
          content: '';
          display: block;
          width: 8px;
          height: 14px;
          border-right: 2.5px solid #fff;
          border-bottom: 2.5px solid #fff;
          transform: rotate(45deg) translate(-1px, -3px);
        }
      }
    }

    &:after {
      content: '';
      width: 1px;
      height: 100%;
      border-left: 1px solid #000;
      flex-shrink: 1;
    }
  }


  .selector_wrapper {
    display: flex;
    flex-direction: column;
    gap: 10px;
    font-family: Roboto, sans-serif;
    font-weight: 600;
    font-size: 20px;
    line-height: 24px;
    letter-spacing: 0;
    margin-bottom: 15px;
    width: 100%;
    animation: stepFadeInUp 0.35s ease both;

    .summary {
      animation: stepFadeInUp 0.3s ease both;
    }

    strong {
      display: block;
      margin-top: 11px;
      @media (max-width: 767px) {
        font-size: 16px;
        font-weight: 600;
        margin-top:3px;
      }

      .section__step_no {
        display: none !important;
        @media (max-width: 767px) {
          /* display: inline; */
          &:after {
            content: " - ";
          }

          &.section__step_no_done {
            display: inline-flex;
            width: 28px;
            height: 28px;
            background-color: #000;
            border-radius: 100%;
            align-items: center;
            justify-content: center;
            vertical-align: middle;
            margin-right: 8px;
            font-size: 0 !important;
            &:after {
              content: '';
              display: block;
              width: 6px;
              height: 11px;
              border-right: 2px solid #fff;
              border-bottom: 2px solid #fff;
              transform: rotate(45deg) translate(-1px, -2px);
            }
          }
        }
      }
    }

    [class^="_color_option"] {

      padding: 0;

      &[class*="_checked"] {
        label {
          &:before {
            display: none;
          }

          [class^='_color_img'] {
            outline: 3px solid #ED1B34;
          }
        }
      }

      label {
        [class^='_title'] {
          display: none;
        }

        img {
          border-radius: 100%;
          width: 51px;
          aspect-ratio: 1 / 1;
          @media (max-width: 499px) {
            width: 61px;
          }
        }
      }
    }
  }

  // &[id="step_3"] h2:after {
  //   display: none;
  // }

  //.vehicle_selector {
  //  display: flex;
  //  flex-direction: row;
  //
  //}


  &.has_bf_2025 .seat_type_selector {
    .button_option {
      label {
        padding-block: 16px;
        @media (max-width: 767px) {
          padding-block: 16px 12px;
        }
      }

      .title {
        @media (max-width: 375px) {
          font-size: 13px;
        }
      }

      .innerPriceContainer {
        border-radius: 2px;
        padding: 2px 4px;

        .btn_savings {
          border-radius: 2px;
        }

        .price_wrapper {


          .price {
            font-size: 16px;
            @media (max-width: 767px) {
              font-size: 14px;
            }
          }

          .compare_price {
            font-size: 14px;
            @media (max-width: 767px) {
              font-size: 12px;
            }
          }
        }
      }
    }
  }


}

.scs_new_design_2026 {
  .btn {
    background: #ED1B34;
    box-shadow: 0 4px #B61C34;

    &:not([disabled]):hover {
      background-color: #B61C34;
    }
  }

  [class="accordion__content-wrapper"] {
    overflow-x: visible;
    scroll-behavior: smooth;
    scroll-snap-type: x mandatory;
    scroll-snap-align: start;
  }
  .checked label {
    border: 0;
    outline: 0;
    background: transparent;
  }
}

.upsells_wrapper {
  width: 100%;
  padding-bottom: 30px;

  .upsells {
    width: 100%;
    [class="slick-dots"] {
      margin: 0;
      left:0;
      bottom: -28px;
      text-align: left;
      li {
        margin: 0 4px;

        button {
          min-width: 24px;
          min-height: 24px;
          width: 24px;
          height: 24px;
          padding: 4px;
        }

        button:before {
          font-size: 12px;
        }
      }
    }

    [class*="slick-prev"],
    [class*="slick-next"] {
      min-width: 28px;
      min-height: 28px;
      width: 28px;
      height: 28px;
      padding: 4px;
      font-size: 0;
      line-height: 0;
      color: transparent;
      overflow: visible;
    }

    [class*="slick-prev"] {
      left: auto;
      right: 36px;
      bottom: -40px;
      top: auto;
      z-index: 11;
    }
    [class*="slick-next"] {
      left:auto;
      right:0;
      bottom: -40px;
      top:auto;
      z-index: 10;
    }

    [class*="slick-prev"]:before, [class*="slick-next"]:before {
      color: #575757 !important;
      width: 15px;
      height: 15px;
      display: block;
      margin: 0 auto;

    }

    [class*="slick-prev"]:before {
      content: url("data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2215%22%20height%3D%2215%22%20viewBox%3D%220%200%2015%2015%22%20fill%3D%22none%22%3E%3Cg%20clip-path%3D%22url(%23clip0_95_350)%22%3E%3Cpath%20d%3D%22M10.3657%200L12.1344%201.76877L6.40317%207.5L12.1344%2013.2312L10.3657%2015L2.86569%207.5L10.3657%200Z%22%20fill%3D%22%23575757%22%2F%3E%3C%2Fg%3E%3Cdefs%3E%3CclipPath%20id%3D%22clip0_95_350%22%3E%3Crect%20width%3D%2215%22%20height%3D%2215%22%20fill%3D%22white%22%20transform%3D%22matrix(-1%200%200%201%2015%200)%22%2F%3E%3C%2FclipPath%3E%3C%2Fdefs%3E%3C%2Fsvg%3E");
    }

    [class*="slick-next"]:before {
      content: url("data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2215%22%20height%3D%2215%22%20viewBox%3D%220%200%2015%2015%22%20fill%3D%22none%22%3E%3Cg%20clip-path%3D%22url(%23clip0_95_354)%22%3E%3Cpath%20d%3D%22M6.63443%200L4.86572%201.76877L10.597%207.5L4.86572%2013.2312L6.63443%2015L14.1344%207.5L6.63443%200Z%22%20fill%3D%22%23575757%22%2F%3E%3C%2Fg%3E%3Cdefs%3E%3CclipPath%20id%3D%22clip0_95_354%22%3E%3Crect%20width%3D%2215%22%20height%3D%2215%22%20fill%3D%22white%22%2F%3E%3C%2FclipPath%3E%3C%2Fdefs%3E%3C%2Fsvg%3E");
    }
    .upsell {
      width: calc(100% - 1px) !important;
    }
  }

}
.scs_new_design_2026 {
  p, span:not([class*="material-icon"]), ul, li, ol {
    font-family: Roboto, sans-serif;
  }
  [class="videos"] {
    gap: 10px !important;
  }
  [class="videos"] [class="video_item"] {
    width: 60px;
  }

  .vehicle_selector {
    gap: 6px !important;
    select {
      @media (min-width:768px) and (max-width:991px) {
        font-size: 14px !important;
        line-height: 1.3em;
        padding: 3px 5px 3px 5px;

      }
    }
  }
  @media (min-width:768px) and (max-width:991px) {
    [class="emoji-benefits-container"] p {
      font-size: 16px;
    }
    [class*="product__text-container--multiple"] {
      gap: 10px;
    }
    [class*="product__text"] span + span {
      font-size: 14px;
    }
  }
  [class*="accordion__title"] {
    font-family: var(--font-family, "Roboto, sans-serif");
    font-size: 16px !important;
  }
  [class*="accordion__content-wrapper"] ul, [class*="accordion__content-wrapper"] li {
    font-size: 16px;
  }
  [class*="accordion__content-wrapper"] h3 strong, [class*="accordion__content-wrapper"] h3 {
    font-family: var(--font-family, "Roboto, sans-serif");
    font-size: 16px;
  }

}

.custom_rear_addons {
  margin-top: 16px;
  margin-bottom: 16px;
  padding: 16px;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border: 1px solid #e0e0e0;
  border-radius: 8px;

  .custom_rear_header {
    display: flex;
    align-items: center;
    gap: 8px;
    font-family: var(--font-family, "Roboto, sans-serif");
    font-size: 14px;
    font-weight: 700;
    color: #171c28;
    margin-bottom: 6px;

    svg {
      flex-shrink: 0;
      color: #666;
    }
  }

  .custom_rear_note {
    font-family: var(--font-family, "Roboto, sans-serif");
    font-size: 12px;
    color: #666;
    margin: 0 0 12px 0;
    line-height: 1.4;
  }

  .button_option {
    margin-bottom: 8px;

    &:last-of-type {
      margin-bottom: 0;
    }

    label {
      background-color: #f5f5f5;
      border: 2px solid #e0e0e0;
      cursor: pointer;
      transition: all 0.2s ease;

      &:hover {
        border-color: #ccc;
      }
    }

    &.checked label {
      border-color: #171c28;
      background-color: #f0f0f0;
    }
  }

  .shipping_note {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 12px;
    padding: 8px 12px;
    background: #fff3cd;
    border: 1px solid #ffc107;
    border-radius: 4px;
    font-family: var(--font-family, "Roboto, sans-serif");
    font-size: 12px;
    color: #856404;
    line-height: 1.4;

    svg {
      flex-shrink: 0;
    }
  }
}
// Pulsing red glow pointing the user to the field they still need to fill in
// (applied by StepNav when Next is pressed while a required dropdown or
// configurator question is incomplete).
.field_attention {
  border-color: #e02b27 !important;
  animation: field_attention_pulse 1.1s ease-in-out 3;
}

// Pulses red glow continuously until the user makes a selection
// (used in the YMM flow where trim/cab is required).
.field_attention_persist {
  border-color: #e02b27 !important;
  animation: field_attention_pulse 1.1s ease-in-out infinite;
}

@keyframes field_attention_pulse {
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(224, 43, 39, 0);
  }
  50% {
    box-shadow: 0 0 10px 3px rgba(224, 43, 39, 0.65);
  }
}
